import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { getFriendRequestCount } from '../services/friendsApi';

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const { user, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useSettings();
  const navigate = useNavigate();

  // Poll for friend request count
  useEffect(() => {
    if (!user) {
      setFriendRequestCount(0);
      return undefined;
    }

    const fetchCount = async () => {
      try {
        const data = await getFriendRequestCount();
        setFriendRequestCount(data.count);
      } catch {
        // Silently fail
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-puzzle-surface shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src={theme === 'dark' ? '/images/logo_with_text_dark_mode.png' : '/images/logo_with_text.png'}
                alt="Puzzlists"
                className="h-24"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-4">
            <Link
              to="/leaderboard"
              className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors"
            >
              All Time Leaderboard
            </Link>
            <Link
              to="/league"
              className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors"
            >
              Leagues
            </Link>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="p-2 rounded-lg text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover transition-colors"
            >
              {theme === 'dark' ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            {user ? (
              <>
                <Link
                  to="/search"
                  className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors"
                >
                  Find Players
                </Link>
                <Link
                  to="/friends"
                  className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors relative"
                >
                  Friends
                  {friendRequestCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {friendRequestCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/my-stats"
                  className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors"
                >
                  My Stats
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin/images"
                    className="text-puzzle-danger hover:text-red-700 px-3 py-2 text-sm font-medium flex items-center gap-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  className="flex items-center gap-3 ml-2 hover:bg-puzzle-hover rounded-lg px-2 py-1 transition-colors"
                  title="Account Settings"
                >
                  <div className="h-10 w-10 rounded-full bg-puzzle-muted flex items-center justify-center text-sm font-medium text-puzzle-text-secondary overflow-hidden flex-shrink-0">
                    {user.avatar ? (
                      <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-puzzle-text">{user.username}</div>
                    <div className="text-xs text-puzzle-text-secondary">
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email}
                    </div>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-puzzle-text-secondary hover:text-puzzle-text text-sm font-medium ml-2"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-puzzle-text hover:text-puzzle-blue px-3 py-2 text-sm font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-puzzle-blue hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              type="button"
              aria-label="Toggle menu"
              className="p-2 rounded-md text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-puzzle-border py-3">
            <div className="space-y-1">
              <Link
                to="/leaderboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
              >
                All Time Leaderboard
              </Link>
              <Link
                to="/league"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
              >
                Leagues
              </Link>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center gap-2 w-full text-left text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
              >
                {theme === 'dark' ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              {user ? (
                <>
                  <Link
                    to="/search"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    Find Players
                  </Link>
                  <Link
                    to="/friends"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    Friends
                    {friendRequestCount > 0 && (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-500 text-white rounded-full">
                        {friendRequestCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my-stats"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    My Stats
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin/images"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-puzzle-danger hover:bg-red-50 px-3 py-2 rounded-md text-base font-medium"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Admin Dashboard
                    </Link>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    Account Settings
                  </Link>
                  <div className="px-3 py-2 text-sm text-puzzle-text-secondary">
                    {user.username || user.email}
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="block w-full text-left text-red-600 hover:bg-red-50 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-puzzle-text hover:bg-puzzle-hover px-3 py-2 rounded-md text-base font-medium"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
